// Full pull of the user's structure into the local cache.
//
// Port of Flutter's sync_service.dart. Ten endpoints, fetched sequentially to
// keep network load light, then written in ONE transaction as a full replace per
// store — so the cache is never half-old/half-new, and a failed sync leaves the
// previous good copy intact.
//
// Storage is IndexedDB rather than SQLite (see schema.js for why). Records are
// { <key columns>, raw: <untouched server row> } — the same idea as the SQLite
// raw_json column, so no server field is ever lost.
//
// WHEN THIS RUNS (see runSyncIfNeeded): only when the server flags the user's
// data as changed (`SinkData = 1`), when the cache is empty, or when it belongs
// to a different user. A normal launch makes ZERO structure calls — that is the
// whole point of the feature.

import api, { clearSinkData } from '../api/api';
import { dbSupported, SYNC_TABLES } from './schema';
import {
  replaceAll, putOne, getOne, countOf, clearAll,
} from './idb';

let running = false;
export const isSyncRunning = () => running;

const num = (row, keys) => {
  for (const k of keys) {
    const v = row[k];
    if (v == null) continue;
    const n = typeof v === 'number' ? v : parseInt(String(v), 10);
    if (!Number.isNaN(n)) return n;
  }
  return null;
};
const str = (row, keys) => {
  for (const k of keys) {
    const v = row[k];
    if (v != null) return String(v);
  }
  return null;
};

// Endpoint -> store, with the mapper from a server row to its key columns.
// `raw` always carries the untouched row.
const PLAN = [
  {
    endpoint: 'GetDeviceTypeForSinkingData', store: 'device_type', noUser: true,
    map: (r) => ({ device_id: num(r, ['DeviceID', 'DeviceId']), raw: r }),
  },
  {
    endpoint: 'GetUserForSinkingDatabyUserID', store: 'users',
    map: (r) => ({
      user_id: num(r, ['UserId']),
      mobile_number: str(r, ['MobileNumber']),
      name: str(r, ['Name']),
      surname: str(r, ['Surname']),
      raw: r,
    }),
  },
  {
    endpoint: 'GetUserLocationMappingForSinkingDatabyUserID', store: 'user_location_mapping',
    map: (r) => ({
      user_id: num(r, ['UserId']),
      location_id: num(r, ['LocationID', 'LocationId']),
      raw: r,
    }),
  },
  {
    endpoint: 'GetLocationForSinkingDatabyUserID', store: 'location',
    map: (r) => ({
      location_id: num(r, ['LocationID', 'LocationId']),
      location_name: str(r, ['LocationName']),
      master_user_id: num(r, ['MasterUserId']),
      raw: r,
    }),
  },
  {
    endpoint: 'GetLocationRoomsMappingForSinkingDatabyUserID', store: 'location_rooms_mapping',
    map: (r) => ({
      user_id: num(r, ['UserId']),
      location_id: num(r, ['LocationID', 'LocationId']),
      room_id: num(r, ['RoomID', 'RoomId']),
      raw: r,
    }),
  },
  {
    endpoint: 'GetRoomsForSinkingDatabyUserID', store: 'rooms',
    map: (r) => ({
      room_id: num(r, ['RoomID', 'RoomId']),
      room_name: str(r, ['RoomName']),
      raw: r,
    }),
  },
  {
    endpoint: 'GetDevicesInstalledForSinkingDatabyUserID', store: 'devices_installed',
    map: (r) => ({
      id: num(r, ['ID', 'Id']),
      unique_number: str(r, ['UniqueNumber']),
      device_id: num(r, ['DeviceID', 'DeviceId']),
      device_name: str(r, ['DeviceName']),
      location_id: num(r, ['LocationID', 'LocationId']),
      room_id: num(r, ['RoomID', 'RoomId']),
      master_user_id: num(r, ['MasterUserId']),
      raw: r,
    }),
  },
  {
    endpoint: 'GetOutputDetailsForSinkingDatabyUserID', store: 'output_details',
    map: (r) => ({
      id: num(r, ['ID', 'Id', 'OutputDetailId']),
      device_installed_id: num(r, ['DeviceInstalledId', 'DeviceInstalledID']),
      unique_number: str(r, ['UniqueNumber']),
      output_index: num(r, ['Output']),
      output_name: str(r, ['OutputName']),
      raw: r,
    }),
  },
  {
    endpoint: 'GetInputDetailsForSinkingDatabyUserID', store: 'input_details',
    map: (r) => ({
      id: num(r, ['ID', 'Id', 'InputDetailId']),
      device_installed_id: num(r, ['DeviceInstalledId', 'DeviceInstalledID']),
      unique_number: str(r, ['UniqueNumber']),
      raw: r,
    }),
  },
  {
    // No sink endpoint exists for UserDeviceMapping; the favourites list is
    // already denormalised, so it is cached directly.
    endpoint: 'GetListoffavouriteDevicesbyUserId', store: 'favourite_devices',
    map: (r) => ({
      unique_number: str(r, ['UniqueNumber']),
      device_installed_id: num(r, ['DeviceInstalledID', 'DeviceInstalledId', 'ID', 'Id']),
      raw: r,
    }),
  },
];

async function fetchRows(endpoint, body) {
  try {
    const res = await api.post(`/${endpoint}`, body, { timeout: 30000 });
    return Array.isArray(res.data) ? res.data : [];
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`[Sync] ${endpoint} failed:`, (e && e.message) || e);
    return [];
  }
}

// A record is only storable if its key is present — IndexedDB rejects a put with
// a null keyPath value, and one bad row would abort the whole transaction.
const hasKey = (rec, store) => {
  if (store === 'device_type') return rec.device_id != null;
  if (store === 'users') return rec.user_id != null;
  if (store === 'user_location_mapping') return rec.user_id != null && rec.location_id != null;
  if (store === 'location') return rec.location_id != null;
  if (store === 'location_rooms_mapping') {
    return rec.user_id != null && rec.location_id != null && rec.room_id != null;
  }
  if (store === 'rooms') return rec.room_id != null;
  if (store === 'favourite_devices') return !!rec.unique_number;
  return rec.id != null;
};

// Pull everything and replace the cache. Returns true on success; never throws.
export async function fullSync(userId) {
  if (running) return false;
  if (!dbSupported() || !userId) return false;

  running = true;
  const started = Date.now();
  try {
    const body = { UserId: userId };
    const entries = [];

    for (const step of PLAN) {
      // eslint-disable-next-line no-await-in-loop
      const rows = await fetchRows(step.endpoint, step.noUser ? {} : body);
      const records = rows
        .filter((r) => r && typeof r === 'object')
        .map(step.map)
        .filter((rec) => hasKey(rec, step.store));
      entries.push({ store: step.store, records, fetched: rows.length });
    }

    await replaceAll(entries.map(({ store, records }) => ({ store, records })));

    await putOne('sync_meta', { key: 'synced_user_id', value: String(userId) });
    await putOne('sync_meta', { key: 'last_full_sync', value: new Date().toISOString() });

    // eslint-disable-next-line no-console
    console.log(`[Sync] done in ${Date.now() - started}ms — `
      + entries.map((e) => `${e.store}=${e.records.length}`).join(' '));
    return true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[Sync] failed:', (e && e.message) || e);
    return false;
  } finally {
    running = false;
  }
}

export async function metaValue(key) {
  if (!dbSupported()) return null;
  try {
    const row = await getOne('sync_meta', key);
    return row ? row.value : null;
  } catch (e) {
    return null;
  }
}

export const lastSyncedAt = () => metaValue('last_full_sync');

export async function hasStructure() {
  if (!dbSupported()) return false;
  try {
    return (await countOf('devices_installed')) > 0;
  } catch (e) {
    return false;
  }
}

export async function tableCounts() {
  if (!dbSupported()) return {};
  const out = {};
  for (const t of SYNC_TABLES) {
    try {
      // eslint-disable-next-line no-await-in-loop
      out[t] = await countOf(t);
    } catch (e) {
      out[t] = -1;
    }
  }
  return out;
}

// Wipe the cache — on logout, so the next user never sees the previous one's data.
export async function clearCache() {
  if (!dbSupported()) return;
  try {
    await clearAll();
  } catch (e) { /* ignore */ }
}

const truthy = (v) => v === 1 || v === '1' || v === true;

// Decide whether a sync is needed, and run it if so.
//
// Three reasons to sync:
//   1. SinkData = 1      the server says this user's structure changed
//   2. empty cache       first run after login
//   3. user mismatch     the cache belongs to somebody else
//
// (3) is NOT in the Flutter version — it writes `synced_user_id` but never reads
// it, so logging in as a second user on the same phone leaves the first user's
// cache in place and the app shows an empty account until something else forces
// a sync. Checking it here is a deliberate fix, not a port omission.
export async function runSyncIfNeeded(user, userId) {
  if (!userId || !dbSupported()) return false;

  const dirty = truthy(user && user.SinkData);
  const empty = !(await hasStructure());
  const syncedFor = await metaValue('synced_user_id');
  const mismatch = syncedFor != null && String(syncedFor) !== String(userId);

  if (!dirty && !empty && !mismatch) return false;
  if (mismatch) await clearCache();

  const ok = await fullSync(userId);

  // Clear the server's dirty flag only after a successful sync, so a failure
  // means we try again next launch instead of losing the signal.
  if (ok && dirty) {
    try { await clearSinkData(userId); } catch (e) { /* ignore */ }
  }
  return ok;
}
